<template>
  <section class="lead-flow-overview-section">
    <h3>Contact</h3>
    <dl>
      <div
        v-for="channel in channels"
        :key="channel.key"
        :class="{
          'lead-flow-contact-details__channel--preferred': isPreferredChannel(channel)
        }"
      >
        <dt class="lead-flow-contact-details__label">
          <img :src="channel.icon" alt="">
          {{ channel.label }}
        </dt>
        <dd class="lead-flow-contact-details__value" :title="channel.value">
          <AppStatusBadge
            v-if="isPreferredChannel(channel)"
            design="new"
            tone="green"
            compact
            class="lead-flow-contact-details__preferred"
            label="Preferred"
          />
          <a
            v-if="channel.href"
            :class="{
              'lead-flow-contact-details__check': channel.isCheckLink
            }"
            :href="channel.href"
            :target="channel.external ? '_blank' : null"
            :rel="channel.external ? 'noopener noreferrer' : null"
          >
            {{ channel.value }}
          </a>
          <span v-else>{{ channel.value }}</span>
          <button
            v-if="channel.copyValue"
            type="button"
            class="lead-flow-contact-details__copy"
            :aria-label="`Copy ${channel.label}`"
            :title="`Copy ${channel.label}`"
            @click="copyContact(channel.copyValue)"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="5.25" y="5.25" width="8.5" height="8.5" rx="1.25" stroke="currentColor" stroke-width="1.5" />
              <path d="M3.25 10.75h-1A1.25 1.25 0 0 1 1 9.5V2.25A1.25 1.25 0 0 1 2.25 1h7.25a1.25 1.25 0 0 1 1.25 1.25v1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </button>
        </dd>
      </div>
    </dl>
  </section>
</template>

<script>
import EmailIcon from '@/assets/img/email-icon.svg'
import PhoneIcon from '@/assets/img/phone-icon.svg'
import TelegramIcon from '@/assets/img/tg-icon.svg'
import WhatsAppIcon from '@/assets/img/wa-icon.svg'

export default {
  name: 'LeadContactDetails',
  props: {
    contact: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    channels() {
      const email = this.normalizeValue(this.contact.email)
      const phone = this.normalizeValue(this.contact.phone)
      const whatsapp = this.normalizeValue(this.contact.whatsapp)
      const telegram = this.normalizeValue(this.contact.telegram)
      const phoneDigits = phone.replace(/\D/g, '')

      return [
        {
          key: 'email',
          label: 'Email',
          icon: EmailIcon,
          value: email || 'Not provided',
          copyValue: email,
          href: email ? `mailto:${email}` : '',
          external: false
        },
        {
          key: 'phone',
          label: 'Phone',
          icon: PhoneIcon,
          value: phone || 'Not provided',
          copyValue: phone,
          href: phone ? `tel:${phone}` : '',
          external: false
        },
        {
          key: 'whatsapp',
          label: 'WhatsApp',
          icon: WhatsAppIcon,
          value: whatsapp || (phoneDigits ? 'Check' : 'Not provided'),
          copyValue: whatsapp,
          href: this.whatsappLink(whatsapp, phoneDigits),
          isCheckLink: !whatsapp && Boolean(phoneDigits),
          external: true
        },
        {
          key: 'telegram',
          label: 'Telegram',
          icon: TelegramIcon,
          value: telegram || (phoneDigits ? 'Check' : 'Not provided'),
          copyValue: telegram,
          href: this.telegramLink(telegram, phoneDigits),
          isCheckLink: !telegram && Boolean(phoneDigits),
          external: true
        }
      ]
    },
    preferredMethod() {
      return this.normalizeValue(this.contact.preferred_method)
    }
  },
  methods: {
    async copyContact(value) {
      try {
        await navigator.clipboard.writeText(value)
        this.$notify({ type: 'success', title: 'Copied to clipboard' })
      } catch (error) {
        this.$notify({ type: 'error', title: 'Unable to copy contact' })
      }
    },
    normalizeValue(value) {
      return typeof value === 'string' ? value.trim() : ''
    },
    isPreferredChannel(channel) {
      const preferredMethod = this.normalizeMethod(this.preferredMethod)
      if (!preferredMethod) return false
      return [channel.key, channel.label].some(value => {
        return this.normalizeMethod(value) === preferredMethod
      })
    },
    normalizeMethod(value) {
      const normalized = this.normalizeValue(value).toLowerCase().replace(/[\s_-]/g, '')
      if (normalized.includes('whatsapp')) return 'whatsapp'
      if (normalized.includes('telegram')) return 'telegram'
      if (normalized.includes('email')) return 'email'
      if (normalized.includes('phone') || normalized.includes('call')) return 'phone'
      return normalized
    },
    whatsappLink(value, fallbackPhone = '') {
      if (!value) {
        return fallbackPhone ? `https://api.whatsapp.com/send/?phone=${fallbackPhone}` : ''
      }
      if (/^https?:\/\//i.test(value)) return value
      const digits = value.replace(/\D/g, '')
      return digits ? `https://api.whatsapp.com/send/?phone=${digits}` : ''
    },
    telegramLink(value, fallbackPhone = '') {
      if (!value) {
        return fallbackPhone ? `https://t.me/+${fallbackPhone}` : ''
      }
      if (/^https?:\/\//i.test(value)) return value
      const username = value.replace(/^@/, '')
      return /^[a-zA-Z0-9_]+$/.test(username) ? `https://t.me/${username}` : ''
    }
  }
}
</script>

<style scoped>
.lead-flow-contact-details__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.lead-flow-contact-details__label img {
  width: 16px;
  height: 16px;
  padding: 2px;
  border-radius: 4px;
  background: #869291;
  object-fit: contain;
}

.lead-flow-contact-details__value {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 7px;
}

.lead-flow-contact-details__copy {
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #d6e0e7;
  border-radius: 6px;
  background: #fff;
  color: #3976ae;
  cursor: pointer;
}

.lead-flow-contact-details__copy:hover,
.lead-flow-contact-details__copy:focus-visible {
  border-color: #3976ae;
  background: #eef5fb;
}

.lead-flow-contact-details__channel--preferred {
  padding-left: 8px;
  border-left: 3px solid #4f7e1d;
}

.lead-flow-overview-section .lead-flow-contact-details__check {
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border: 1px solid #3976ae;
  border-radius: 6px;
  color: #3976ae;
  font-weight: 700;
  line-height: 18px;
  text-decoration: none;
}

.lead-flow-overview-section .lead-flow-contact-details__check:hover {
  background: #3976ae;
  color: #fff;
  text-decoration: none;
}

.lead-flow-overview-section .lead-flow-contact-details__value .lead-flow-contact-details__preferred {
  display: inline-flex;
  font-size: 12px !important;
}
</style>
